/**
 * AI-Driven Campaign Generation Service
 * Generates optimized campaigns from A/B test winners with multi-generational tracking
 */

import { Template, ABTest } from '../types/template';
import { WinnerDetectionResult } from './winnerDetectionEngine';

export interface CampaignGenerationConfig {
  enableAutoGeneration: boolean;
  confidenceThreshold: number;
  minimumImprovementThreshold: number;
  maxGenerationsPerTemplate: number;
  personalizationEnabled: boolean;
  audienceSegmentationEnabled: boolean;
  performancePredictionEnabled: boolean;
}

export interface GeneratedCampaign {
  id: string;
  name: string;
  sourceTestId: string;
  sourceTemplateId: string;
  generation: number;
  parentCampaignId?: string;
  template: Template;
  subjectLines: OptimizedSubjectLine[];
  audienceSegments: AudienceSegment[];
  personalizationRules: PersonalizationRule[];
  performancePrediction: PerformancePrediction;
  metadata: {
    createdAt: Date;
    createdBy: string;
    winnerConfidence: number;
    expectedImprovement: number;
    tags: string[];
  };
}

export interface OptimizedSubjectLine {
  id: string;
  text: string;
  category: string;
  performance: {
    predictedOpenRate: number;
    confidence: number;
    historicalPerformance?: {
      openRate: number;
      testCount: number;
      lastTested: Date;
    };
  };
  personalizationTokens: string[];
  emotionalTriggers: EmotionalTrigger[];
  variations: string[];
}

export interface EmotionalTrigger {
  type: 'urgency' | 'curiosity' | 'social_proof' | 'fear_of_missing_out' | 'exclusivity' | 'benefit_focused';
  strength: number; // 0-100
  keywords: string[];
}

export interface AudienceSegment {
  id: string;
  name: string;
  criteria: SegmentCriteria[];
  size: number;
  expectedPerformance: {
    openRate: number;
    clickRate: number;
    conversionRate: number;
  };
  optimizations: SegmentOptimization[];
}

export interface SegmentCriteria {
  field: string;
  operator: 'equals' | 'contains' | 'greater_than' | 'less_than' | 'in' | 'not_in';
  value: any;
  weight: number;
}

export interface SegmentOptimization {
  type: 'content' | 'timing' | 'frequency' | 'channel';
  description: string;
  expectedImpact: number;
  confidence: number;
}

export interface PersonalizationRule {
  id: string;
  name: string;
  condition: string;
  contentVariations: ContentVariation[];
  priority: number;
  expectedLift: number;
}

export interface ContentVariation {
  blockId: string;
  originalContent: any;
  personalizedContent: any;
  trigger: string;
  confidence: number;
}

export interface PerformancePrediction {
  openRate: {
    predicted: number;
    confidence: number;
    range: [number, number];
  };
  clickRate: {
    predicted: number;
    confidence: number;
    range: [number, number];
  };
  conversionRate: {
    predicted: number;
    confidence: number;
    range: [number, number];
  };
  revenueImpact: {
    predicted: number;
    confidence: number;
    timeframe: 'daily' | 'weekly' | 'monthly';
  };
  riskFactors: RiskFactor[];
  optimizationOpportunities: OptimizationOpportunity[];
}

export interface RiskFactor {
  type: 'audience_fatigue' | 'seasonal_decline' | 'market_saturation' | 'competitive_pressure';
  probability: number;
  impact: number;
  mitigation: string;
}

export interface OptimizationOpportunity {
  type: 'content' | 'timing' | 'segmentation' | 'personalization';
  description: string;
  expectedImprovement: number;
  effort: 'low' | 'medium' | 'high';
  priority: number;
}

export interface TemplateEvolution {
  templateId: string;
  generations: TemplateGeneration[];
  evolutionPath: EvolutionStep[];
  performanceTrajectory: PerformancePoint[];
  learnings: EvolutionLearning[];
}

export interface TemplateGeneration {
  generation: number;
  templateSnapshot: Template;
  sourceTestId?: string;
  parentGeneration?: number;
  createdAt: Date;
  performance: GenerationPerformance;
  improvements: string[];
  regressions: string[];
}

export interface EvolutionStep {
  fromGeneration: number;
  toGeneration: number;
  changes: EvolutionChange[];
  reason: string;
  confidence: number;
  actualImprovement?: number;
}

export interface EvolutionChange {
  blockId: string;
  changeType: 'content' | 'style' | 'structure' | 'personalization';
  description: string;
  oldValue: any;
  newValue: any;
  expectedImpact: number;
}

export interface PerformancePoint {
  generation: number;
  timestamp: Date;
  metrics: {
    openRate: number;
    clickRate: number;
    conversionRate: number;
    revenue: number;
  };
  sampleSize: number;
}

export interface EvolutionLearning {
  id: string;
  type: 'pattern' | 'anti_pattern' | 'optimization' | 'constraint';
  description: string;
  confidence: number;
  applicability: string[];
  examples: string[];
}

export interface GenerationPerformance {
  openRate: number;
  clickRate: number;
  conversionRate: number;
  revenue: number;
  sampleSize: number;
  testDuration: number;
  confidence: number;
  comparedToParent?: {
    improvement: number;
    significance: number;
  };
}

export class CampaignGenerationService {
  private config: CampaignGenerationConfig;
  private templateEvolutions: Map<string, TemplateEvolution> = new Map();
  private generatedCampaigns: Map<string, GeneratedCampaign> = new Map();
  private subjectLineRepository: Map<string, OptimizedSubjectLine[]> = new Map();

  constructor(config: CampaignGenerationConfig) {
    this.config = config;
    this.initializeSubjectLineRepository();
  }

  /**
   * Generate optimized campaign from A/B test winner
   */
  public async generateCampaignFromWinner(
    test: ABTest,
    winnerResult: WinnerDetectionResult,
    originalTemplate: Template
  ): Promise<GeneratedCampaign> {
    if (!this.config.enableAutoGeneration) {
      throw new Error('Auto-generation is disabled');
    }

    if (winnerResult.confidence < this.config.confidenceThreshold) {
      throw new Error(`Winner confidence ${winnerResult.confidence}% below threshold ${this.config.confidenceThreshold}%`);
    }

    if (winnerResult.improvement < this.config.minimumImprovementThreshold) {
      throw new Error(`Improvement ${winnerResult.improvement}% below threshold ${this.config.minimumImprovementThreshold}%`);
    }

    const campaignId = `campaign-${test.id}-${Date.now()}`;

    // Extract base template ID (remove generation suffix if present)
    const baseTemplateId = this.extractBaseTemplateId(originalTemplate.id);
    const evolution = this.getOrCreateTemplateEvolution(baseTemplateId);
    const currentGeneration = evolution.generations.length;

    // Check generation limit
    if (currentGeneration >= this.config.maxGenerationsPerTemplate) {
      throw new Error(`Maximum generations (${this.config.maxGenerationsPerTemplate}) reached for template ${baseTemplateId}`);
    }

    // Create evolved template from winner
    const evolvedTemplate = await this.evolveTemplate(originalTemplate, test, winnerResult, currentGeneration + 1);

    // Generate optimized subject lines
    const subjectLines = await this.generateOptimizedSubjectLines(
      originalTemplate.category,
      test,
      winnerResult
    );

    // Create audience segments
    const audienceSegments = this.config.audienceSegmentationEnabled
      ? await this.generateAudienceSegments(test, winnerResult)
      : [];

    // Create personalization rules
    const personalizationRules = this.config.personalizationEnabled
      ? await this.generatePersonalizationRules(evolvedTemplate, test, winnerResult)
      : [];

    // Predict performance
    const performancePrediction = this.config.performancePredictionEnabled
      ? await this.predictCampaignPerformance(evolvedTemplate, subjectLines, audienceSegments)
      : this.createDefaultPerformancePrediction();

    const campaign: GeneratedCampaign = {
      id: campaignId,
      name: `${originalTemplate.name} - Generation ${currentGeneration + 1}`,
      sourceTestId: test.id,
      sourceTemplateId: originalTemplate.id,
      generation: currentGeneration + 1,
      parentCampaignId: this.findParentCampaignId(originalTemplate.id, currentGeneration),
      template: evolvedTemplate,
      subjectLines,
      audienceSegments,
      personalizationRules,
      performancePrediction,
      metadata: {
        createdAt: new Date(),
        createdBy: 'ai-campaign-generator',
        winnerConfidence: winnerResult.confidence,
        expectedImprovement: winnerResult.improvement,
        tags: ['ai-generated', 'winner-based', `generation-${currentGeneration + 1}`]
      }
    };

    // Store campaign
    this.generatedCampaigns.set(campaignId, campaign);

    // Update template evolution
    this.updateTemplateEvolution(originalTemplate.id, evolvedTemplate, test, winnerResult, currentGeneration + 1);

    // Update subject line repository
    this.updateSubjectLineRepository(originalTemplate.category, subjectLines);

    return campaign;
  }

  /**
   * Get template evolution history
   */
  public getTemplateEvolution(templateId: string): TemplateEvolution | null {
    return this.templateEvolutions.get(templateId) || null;
  }

  /**
   * Get optimized subject lines for category
   */
  public getOptimizedSubjectLines(category: string, limit: number = 10): OptimizedSubjectLine[] {
    const subjectLines = this.subjectLineRepository.get(category) || [];
    return subjectLines
      .sort((a, b) => b.performance.predictedOpenRate - a.performance.predictedOpenRate)
      .slice(0, limit);
  }

  /**
   * Get generated campaigns
   */
  public getGeneratedCampaigns(templateId?: string): GeneratedCampaign[] {
    const campaigns = Array.from(this.generatedCampaigns.values());
    return templateId
      ? campaigns.filter(c => c.sourceTemplateId === templateId)
      : campaigns;
  }

  /**
   * Predict campaign performance
   */
  public async predictCampaignPerformance(
    template: Template,
    subjectLines: OptimizedSubjectLine[],
    audienceSegments: AudienceSegment[]
  ): Promise<PerformancePrediction> {
    // Use historical data and ML models to predict performance
    const baseOpenRate = this.calculateBaseOpenRate(template.category);
    const subjectLineBoost = this.calculateSubjectLineBoost(subjectLines);
    const segmentationBoost = this.calculateSegmentationBoost(audienceSegments);

    const predictedOpenRate = baseOpenRate * (1 + subjectLineBoost + segmentationBoost);
    const predictedClickRate = predictedOpenRate * 0.15; // Simplified calculation
    const predictedConversionRate = predictedClickRate * 0.05; // Simplified calculation

    return {
      openRate: {
        predicted: predictedOpenRate,
        confidence: 75,
        range: [predictedOpenRate * 0.9, predictedOpenRate * 1.1]
      },
      clickRate: {
        predicted: predictedClickRate,
        confidence: 70,
        range: [predictedClickRate * 0.85, predictedClickRate * 1.15]
      },
      conversionRate: {
        predicted: predictedConversionRate,
        confidence: 65,
        range: [predictedConversionRate * 0.8, predictedConversionRate * 1.2]
      },
      revenueImpact: {
        predicted: predictedConversionRate * 100, // Simplified calculation
        confidence: 60,
        timeframe: 'monthly'
      },
      riskFactors: this.identifyRiskFactors(template),
      optimizationOpportunities: this.identifyOptimizationOpportunities(template, subjectLines, audienceSegments)
    };
  }

  private async evolveTemplate(
    originalTemplate: Template,
    test: ABTest,
    winnerResult: WinnerDetectionResult,
    generation: number
  ): Promise<Template> {
    const winnerVariant = test.variants.find(v => v.id === winnerResult.winnerVariantId);
    if (!winnerVariant) {
      throw new Error('Winner variant not found');
    }

    // Create evolved template by applying winner changes
    const evolvedTemplate: Template = {
      ...originalTemplate,
      id: `${originalTemplate.id}-gen-${generation}`,
      name: `${originalTemplate.name} - Generation ${generation}`,
      content: this.applyWinnerChangesToContent(originalTemplate.content, winnerVariant.changes),
      metadata: {
        ...originalTemplate.metadata,
        version: generation,
        updatedAt: new Date(),
        tags: [...originalTemplate.metadata.tags, `generation-${generation}`, 'ai-evolved']
      }
    };

    return evolvedTemplate;
  }

  private applyWinnerChangesToContent(content: any[], changes: any[]): any[] {
    const updatedContent = JSON.parse(JSON.stringify(content));

    changes.forEach(change => {
      const blockIndex = updatedContent.findIndex((block: any) => block.id === change.blockId);
      if (blockIndex !== -1) {
        // Apply the change based on change type
        switch (change.changeType) {
          case 'content':
            updatedContent[blockIndex].content = change.newValue;
            break;
          case 'style':
            updatedContent[blockIndex].styles = { ...updatedContent[blockIndex].styles, ...change.newValue };
            break;
          case 'position':
            updatedContent[blockIndex].position = change.newValue;
            break;
          case 'visibility':
            updatedContent[blockIndex].metadata.hidden = !change.newValue;
            break;
        }
      }
    });

    return updatedContent;
  }

  private async generateOptimizedSubjectLines(
    category: string,
    test: ABTest,
    winnerResult: WinnerDetectionResult
  ): Promise<OptimizedSubjectLine[]> {
    const subjectLines: OptimizedSubjectLine[] = [];

    // Extract winning subject line patterns from test
    const winnerVariant = test.variants.find(v => v.id === winnerResult.winnerVariantId);
    if (winnerVariant) {
      const subjectLineChanges = winnerVariant.changes.filter(c =>
        c.blockId.toLowerCase().includes('subject') ||
        c.changeType === 'content' && c.description.toLowerCase().includes('subject')
      );

      subjectLineChanges.forEach((change, index) => {
        const subjectLine: OptimizedSubjectLine = {
          id: `sl-${category}-${Date.now()}-${index}`,
          text: change.newValue,
          category,
          performance: {
            predictedOpenRate: this.calculateBaseOpenRate(category) * (1 + winnerResult.improvement / 100),
            confidence: winnerResult.confidence,
            historicalPerformance: {
              openRate: 25.5, // From test results
              testCount: 1,
              lastTested: new Date()
            }
          },
          personalizationTokens: this.extractPersonalizationTokens(change.newValue),
          emotionalTriggers: this.analyzeEmotionalTriggers(change.newValue),
          variations: this.generateSubjectLineVariations(change.newValue)
        };

        subjectLines.push(subjectLine);
      });
    }

    // Generate additional AI-powered variations
    const aiGeneratedLines = this.generateAISubjectLines(category, winnerResult);
    subjectLines.push(...aiGeneratedLines);

    return subjectLines;
  }

  private generateAISubjectLines(category: string, _winnerResult: WinnerDetectionResult): OptimizedSubjectLine[] {
    const templates = {
      promotional: [
        "Don't miss out - {offer} ends soon!",
        "Exclusive {discount}% off - Limited time!",
        "Your {benefit} is waiting inside",
        "Last chance: {offer} expires today"
      ],
      newsletter: [
        "This week's top {topic} insights",
        "{number} things you need to know about {topic}",
        "Weekly roundup: {topic} edition",
        "Your {topic} update is here"
      ],
      transactional: [
        "Your {action} confirmation",
        "Action required: {task}",
        "Update on your {item}",
        "{status} - Next steps inside"
      ],
      welcome: [
        "Welcome to {brand} - Let's get started!",
        "You're in! Here's what happens next",
        "Welcome aboard - Your journey begins now",
        "Thanks for joining {brand}!"
      ]
    };

    const categoryTemplates = templates[category as keyof typeof templates] || templates.promotional;

    return categoryTemplates.map((template, index) => ({
      id: `ai-sl-${category}-${Date.now()}-${index}`,
      text: template,
      category,
      performance: {
        predictedOpenRate: this.calculateBaseOpenRate(category) * 1.1,
        confidence: 70
      },
      personalizationTokens: this.extractPersonalizationTokens(template),
      emotionalTriggers: this.analyzeEmotionalTriggers(template),
      variations: this.generateSubjectLineVariations(template)
    }));
  }

  private async generateAudienceSegments(
    _test: ABTest,
    _winnerResult: WinnerDetectionResult
  ): Promise<AudienceSegment[]> {
    // Generate audience segments based on test performance patterns
    const segments: AudienceSegment[] = [
      {
        id: 'high-engagement',
        name: 'High Engagement Users',
        criteria: [
          { field: 'engagement_score', operator: 'greater_than', value: 70, weight: 1.0 },
          { field: 'last_open_days', operator: 'less_than', value: 7, weight: 0.8 }
        ],
        size: 1000,
        expectedPerformance: {
          openRate: 35.0,
          clickRate: 8.5,
          conversionRate: 2.1
        },
        optimizations: [
          {
            type: 'content',
            description: 'Use advanced content and detailed information',
            expectedImpact: 15,
            confidence: 80
          }
        ]
      },
      {
        id: 'new-subscribers',
        name: 'New Subscribers',
        criteria: [
          { field: 'subscription_days', operator: 'less_than', value: 30, weight: 1.0 }
        ],
        size: 500,
        expectedPerformance: {
          openRate: 28.0,
          clickRate: 6.2,
          conversionRate: 1.8
        },
        optimizations: [
          {
            type: 'content',
            description: 'Focus on onboarding and education',
            expectedImpact: 20,
            confidence: 75
          }
        ]
      }
    ];

    return segments;
  }

  private async generatePersonalizationRules(
    _template: Template,
    _test: ABTest,
    _winnerResult: WinnerDetectionResult
  ): Promise<PersonalizationRule[]> {
    const rules: PersonalizationRule[] = [
      {
        id: 'name-personalization',
        name: 'Name Personalization',
        condition: 'user.firstName != null',
        contentVariations: [
          {
            blockId: 'header-greeting',
            originalContent: 'Hello there!',
            personalizedContent: 'Hello {{firstName}}!',
            trigger: 'has_first_name',
            confidence: 85
          }
        ],
        priority: 1,
        expectedLift: 12
      },
      {
        id: 'location-based',
        name: 'Location-Based Content',
        condition: 'user.location != null',
        contentVariations: [
          {
            blockId: 'offer-content',
            originalContent: 'Special offer available',
            personalizedContent: 'Special offer available in {{location}}',
            trigger: 'has_location',
            confidence: 70
          }
        ],
        priority: 2,
        expectedLift: 8
      }
    ];

    return rules;
  }

  private getOrCreateTemplateEvolution(templateId: string): TemplateEvolution {
    let evolution = this.templateEvolutions.get(templateId);

    if (!evolution) {
      evolution = {
        templateId,
        generations: [],
        evolutionPath: [],
        performanceTrajectory: [],
        learnings: []
      };
      this.templateEvolutions.set(templateId, evolution);
    }

    return evolution;
  }

  private extractBaseTemplateId(templateId: string): string {
    // Remove generation suffix if present (e.g., "template-1-gen-2" -> "template-1")
    return templateId.replace(/-gen-\d+$/, '');
  }

  private updateTemplateEvolution(
    templateId: string,
    evolvedTemplate: Template,
    test: ABTest,
    winnerResult: WinnerDetectionResult,
    generation: number
  ): void {
    const baseTemplateId = this.extractBaseTemplateId(templateId);
    const evolution = this.templateEvolutions.get(baseTemplateId)!;

    const newGeneration: TemplateGeneration = {
      generation,
      templateSnapshot: evolvedTemplate,
      sourceTestId: test.id,
      parentGeneration: generation - 1,
      createdAt: new Date(),
      performance: {
        openRate: 25.5, // From test results
        clickRate: 6.2,
        conversionRate: 1.8,
        revenue: 150,
        sampleSize: 1000,
        testDuration: 14,
        confidence: winnerResult.confidence,
        comparedToParent: {
          improvement: winnerResult.improvement,
          significance: winnerResult.confidence
        }
      },
      improvements: [`${winnerResult.improvement.toFixed(1)}% improvement in primary metric`],
      regressions: []
    };

    evolution.generations.push(newGeneration);

    // Add evolution step
    if (generation > 1) {
      const evolutionStep: EvolutionStep = {
        fromGeneration: generation - 1,
        toGeneration: generation,
        changes: this.extractEvolutionChanges(test, winnerResult),
        reason: `A/B test winner implementation with ${winnerResult.confidence}% confidence`,
        confidence: winnerResult.confidence,
        actualImprovement: winnerResult.improvement
      };

      evolution.evolutionPath.push(evolutionStep);
    }

    // Add performance point
    evolution.performanceTrajectory.push({
      generation,
      timestamp: new Date(),
      metrics: {
        openRate: newGeneration.performance.openRate,
        clickRate: newGeneration.performance.clickRate,
        conversionRate: newGeneration.performance.conversionRate,
        revenue: newGeneration.performance.revenue
      },
      sampleSize: newGeneration.performance.sampleSize
    });

    // Extract learnings
    const learning: EvolutionLearning = {
      id: `learning-${templateId}-${generation}`,
      type: 'optimization',
      description: `Generation ${generation} showed ${winnerResult.improvement.toFixed(1)}% improvement through winner implementation`,
      confidence: winnerResult.confidence,
      applicability: [evolvedTemplate.category],
      examples: [`Test ${test.id} winner implementation`]
    };

    evolution.learnings.push(learning);
  }

  private extractEvolutionChanges(test: ABTest, winnerResult: WinnerDetectionResult): EvolutionChange[] {
    const winnerVariant = test.variants.find(v => v.id === winnerResult.winnerVariantId);
    if (!winnerVariant) return [];

    return winnerVariant.changes.map(change => ({
      blockId: change.blockId,
      changeType: change.changeType as any,
      description: change.description,
      oldValue: change.oldValue,
      newValue: change.newValue,
      expectedImpact: change.confidence || 50
    }));
  }

  private findParentCampaignId(templateId: string, generation: number): string | undefined {
    const campaigns = Array.from(this.generatedCampaigns.values());
    const baseTemplateId = this.extractBaseTemplateId(templateId);
    const parentCampaign = campaigns.find(c =>
      this.extractBaseTemplateId(c.sourceTemplateId) === baseTemplateId && c.generation === generation
    );
    return parentCampaign?.id;
  }

  private updateSubjectLineRepository(category: string, subjectLines: OptimizedSubjectLine[]): void {
    const existing = this.subjectLineRepository.get(category) || [];
    const updated = [...existing];

    subjectLines.forEach(newLine => {
      const existingIndex = updated.findIndex(line => line.text === newLine.text);
      if (existingIndex >= 0) {
        // Update existing line with better performance data
        if (newLine.performance.confidence > updated[existingIndex].performance.confidence) {
          updated[existingIndex] = newLine;
        }
      } else {
        updated.push(newLine);
      }
    });

    // Keep only top performing subject lines (max 50 per category)
    updated.sort((a, b) => b.performance.predictedOpenRate - a.performance.predictedOpenRate);
    this.subjectLineRepository.set(category, updated.slice(0, 50));
  }

  private calculateBaseOpenRate(category: string): number {
    const baseRates = {
      promotional: 22.0,
      newsletter: 25.0,
      transactional: 45.0,
      welcome: 35.0,
      announcement: 28.0,
      custom: 24.0
    };
    return baseRates[category as keyof typeof baseRates] || 24.0;
  }

  private calculateSubjectLineBoost(subjectLines: OptimizedSubjectLine[]): number {
    if (subjectLines.length === 0) return 0;

    const avgConfidence = subjectLines.reduce((sum, sl) => sum + sl.performance.confidence, 0) / subjectLines.length;
    return (avgConfidence - 50) / 1000; // Convert to decimal boost
  }

  private calculateSegmentationBoost(segments: AudienceSegment[]): number {
    if (segments.length === 0) return 0;

    const avgExpectedImprovement = segments.reduce((sum, seg) =>
      sum + seg.optimizations.reduce((optSum, opt) => optSum + opt.expectedImpact, 0), 0
    ) / segments.length;

    return avgExpectedImprovement / 1000; // Convert to decimal boost
  }

  private identifyRiskFactors(_template: Template): RiskFactor[] {
    return [
      {
        type: 'audience_fatigue',
        probability: 30,
        impact: 15,
        mitigation: 'Rotate content themes and vary send frequency'
      },
      {
        type: 'seasonal_decline',
        probability: 20,
        impact: 10,
        mitigation: 'Adjust content for seasonal relevance'
      }
    ];
  }

  private identifyOptimizationOpportunities(
    _template: Template,
    _subjectLines: OptimizedSubjectLine[],
    _segments: AudienceSegment[]
  ): OptimizationOpportunity[] {
    return [
      {
        type: 'personalization',
        description: 'Add dynamic name personalization to increase engagement',
        expectedImprovement: 12,
        effort: 'low',
        priority: 1
      },
      {
        type: 'segmentation',
        description: 'Create behavior-based segments for better targeting',
        expectedImprovement: 18,
        effort: 'medium',
        priority: 2
      }
    ];
  }

  private extractPersonalizationTokens(text: string): string[] {
    const tokens: string[] = [];
    const tokenRegex = /\{(\w+)\}/g;
    let match;

    while ((match = tokenRegex.exec(text)) !== null) {
      tokens.push(match[1]);
    }

    return tokens;
  }

  private analyzeEmotionalTriggers(text: string): EmotionalTrigger[] {
    const triggers: EmotionalTrigger[] = [];
    const lowerText = text.toLowerCase();

    // Urgency triggers
    const urgencyKeywords = ['limited', 'expires', 'hurry', 'last chance', 'ending soon', 'don\'t miss'];
    const urgencyMatches = urgencyKeywords.filter(keyword => lowerText.includes(keyword));
    if (urgencyMatches.length > 0) {
      triggers.push({
        type: 'urgency',
        strength: Math.min(urgencyMatches.length * 30, 100),
        keywords: urgencyMatches
      });
    }

    // Curiosity triggers
    const curiosityKeywords = ['secret', 'revealed', 'discover', 'find out', 'you won\'t believe'];
    const curiosityMatches = curiosityKeywords.filter(keyword => lowerText.includes(keyword));
    if (curiosityMatches.length > 0) {
      triggers.push({
        type: 'curiosity',
        strength: Math.min(curiosityMatches.length * 25, 100),
        keywords: curiosityMatches
      });
    }

    return triggers;
  }

  private generateSubjectLineVariations(original: string): string[] {
    const variations: string[] = [];

    // Add emoji variations
    variations.push(original + ' 🎉');
    variations.push(original + ' ⚡');

    // Add urgency variations
    if (!original.toLowerCase().includes('limited')) {
      variations.push('Limited time: ' + original);
    }

    // Add question variations
    if (!original.includes('?')) {
      variations.push(original.replace(/[.!]$/, '?'));
    }

    return variations.filter(v => v !== original);
  }

  private createDefaultPerformancePrediction(): PerformancePrediction {
    return {
      openRate: { predicted: 24.0, confidence: 50, range: [20.0, 28.0] },
      clickRate: { predicted: 3.6, confidence: 50, range: [3.0, 4.2] },
      conversionRate: { predicted: 0.18, confidence: 50, range: [0.15, 0.21] },
      revenueImpact: { predicted: 100, confidence: 40, timeframe: 'monthly' },
      riskFactors: [],
      optimizationOpportunities: []
    };
  }

  private initializeSubjectLineRepository(): void {
    // Initialize with some demo data
    const categories = ['promotional', 'newsletter', 'transactional', 'welcome'];

    categories.forEach(category => {
      const subjectLines: OptimizedSubjectLine[] = [
        {
          id: `demo-${category}-1`,
          text: `Your ${category} update is here!`,
          category,
          performance: {
            predictedOpenRate: this.calculateBaseOpenRate(category),
            confidence: 75
          },
          personalizationTokens: [],
          emotionalTriggers: [],
          variations: []
        }
      ];

      this.subjectLineRepository.set(category, subjectLines);
    });
  }
}

// Default configuration
export const DEFAULT_CAMPAIGN_GENERATION_CONFIG: CampaignGenerationConfig = {
  enableAutoGeneration: true,
  confidenceThreshold: 90,
  minimumImprovementThreshold: 5,
  maxGenerationsPerTemplate: 10,
  personalizationEnabled: true,
  audienceSegmentationEnabled: true,
  performancePredictionEnabled: true
};